/**
 * This file contains our top-level client-side routes for react-router.
 *
 * Whenever a new top-level route is needed, this is where you should add it.
 * Make sure that the `path` prop for a route is declared on its own line.
 */
import * as React from "react";
import {Route, Switch} from "react-router-dom";

import SmartRedirect from "#shared/app-shell/smart-redirect.tsx";

import ClassroomRedirectRoute from "./classroom-redirect-route.tsx";
import HomepageRoute from "./homepage-route.tsx";
import LocaleRequiredRoute from "./locale-required-route.tsx";
import LoginRequiredRoute from "./login-required-route.tsx";
import PermissionRequiredRoute from "./permission-required-route.tsx";
import {PROGRAM_DOMAINS_MATCHING_GROUP} from "./program-domains.ts";
import TopLevelContentRoute from "./top-level-content-route.tsx";
import TopLevelRoute from "./top-level-route.tsx";

const AboutDocs = React.lazy(() => import("#about-appshell/about-docs.tsx"));
const AccessibilityStatementPage = React.lazy(
    () => import("#about-appshell/accessibility-statement-page.tsx"),
);
const Careers = React.lazy(() => import("#about-appshell/careers-page.tsx"));
const Contribute = React.lazy(() => import("#about-appshell/contribute.tsx"));
const ContributeCreditsPage = React.lazy(
    () => import("#about-appshell/contribute-credits-page.tsx"),
);
const Cookie = React.lazy(() => import("#about-appshell/cookie-page.tsx"));
const Dmca = React.lazy(() => import("#about-appshell/dmca.tsx"));
const DownloadsPage = React.lazy(
    () => import("#about-appshell/downloads-page.tsx"),
);
const Interns = React.lazy(() => import("#about-appshell/interns-page.tsx"));
const OurBoard = React.lazy(() => import("#about-appshell/our-board.tsx"));
const OurSpecialists = React.lazy(
    () => import("#about-appshell/our-content-specialists.tsx"),
);
const OurInterns = React.lazy(() => import("#about-appshell/our-interns.tsx"));
const OurSupporters = React.lazy(
    () => import("#about-appshell/our-supporters.tsx"),
);
const PrivacyPolicy = React.lazy(
    () => import("#about-appshell/privacy-policy.tsx"),
);
const TermsOfServiceClever = React.lazy(
    () => import("#about-appshell/tos-clever.tsx"),
);
const OurAlumni = React.lazy(
    () => import("#about-appshell/team-page/our-alumni.tsx"),
);
const TheTeam = React.lazy(
    () => import("#about-appshell/team-page/the-team.tsx"),
);
const AuthRoutes = React.lazy(() => import("#auth/pages/auth-routes.tsx"));
const BadgesPage = React.lazy(() => import("#badges-package/badges-page.tsx"));
const BlooketLanding = React.lazy(
    () => import("#coach-dashboard-package/khanmigo/blooket-landing.tsx"),
);
const BnccPage = React.lazy(() => import("#bncc-package/bncc-page.tsx"));
const CheckAccess = React.lazy(
    () => import("#check-access-package/check-access.tsx"),
);
const ClassShell = React.lazy(() => import("#class-package/class-shell.tsx"));
const CoachClassRouteRedirect = React.lazy(
    () => import("#class-package/coach-class-route-redirect.tsx"),
);
const UnitMasteryProgress = React.lazy(
    () => import("#class-package/new-progress/unit-mastery-progress.tsx"),
);
const CleverRedirectPage = React.lazy(
    () => import("#clever-package/clever-redirect/clever-redirect-page.tsx"),
);
const CnccPage = React.lazy(() => import("#cncc-package/cncc-page.tsx"));
const TeacherDocumentPage = React.lazy(
    () => import("#guide-teacher-tools/document/teacher-document-page.tsx"),
);
const AIToolPage = React.lazy(
    () => import("#coach-dashboard-package/khanmigo/ai-tool-page.tsx"),
);
const Coach = React.lazy(() => import("#coach-dashboard-package/coach.tsx"));
const CoachReport = React.lazy(
    () => import("#coach-report-package/coach-report.tsx"),
);
const ExerciseReport = React.lazy(
    () => import("#coach-report-package/exercise-report.tsx"),
);
const ContentLibraryCurationPage = React.lazy(
    () => import("#content-library-legacy/content-library-curation-page.tsx"),
);
const ContentLibraryDomainPage = React.lazy(
    () => import("#content-library-legacy/content-library-domain-page.tsx"),
);
const ContentPageRoutes = React.lazy(
    () => import("#content-library/ui/content-page-routes.tsx"),
);
const ContentRedirect = React.lazy(
    () => import("#content-library/ui/content-redirect.tsx"),
);
const HelpRequestsPage = React.lazy(
    () => import("#content-library/ui/help-requests-page.tsx"),
);
const ProgramGalleryPage = React.lazy(
    () => import("#content-library/ui/program-gallery-page.tsx"),
);
const CourseDiscussionWrapper = React.lazy(
    () => import("#course-discussion-package/course-discussion-wrapper.tsx"),
);
const DistrictsAccountLinkPage = React.lazy(
    () =>
        import(
            "#districts-account-link-package/district-account-link-page.tsx"
        ),
);
const ClasslinkRedirectPage = React.lazy(
    () =>
        import(
            "#districts-package/classlink-login/classlink-redirect-page.tsx"
        ),
);
const DistrictsAccountChoicePage = React.lazy(
    () =>
        import(
            "#districts-package/join-page/existing-account-pages/districts-account-choice-page.tsx"
        ),
);
const DistrictsDisconnectSchoolEmailPage = React.lazy(
    () =>
        import(
            "#districts-package/join-page/existing-account-pages/districts-disconnect-school-email-page.tsx"
        ),
);
const DistrictsSchoolLoginPage = React.lazy(
    () =>
        import(
            "#districts-package/join-page/existing-account-pages/districts-school-login-page.tsx"
        ),
);
const DistrictsJoinFinishPage = React.lazy(
    () =>
        import(
            "#districts-package/join-page/finish-page/districts-join-finish-page.tsx"
        ),
);
const DistrictsJoinStartPage = React.lazy(
    () => import("#districts-package/join-page/districts-join-start-page.tsx"),
);
const DistrictsLoginPage = React.lazy(
    () =>
        import(
            "#districts-package/join-page/login-or-signup-page/districts-join-login-page.tsx"
        ),
);
const DistrictsSignupPage = React.lazy(
    () =>
        import(
            "#districts-package/join-page/login-or-signup-page/districts-join-signup-page.tsx"
        ),
);
const DistrictsJoinEmailPage = React.lazy(
    () =>
        import(
            "#districts-package/join-page/school-email-page/districts-join-school-email-page.tsx"
        ),
);
const DistrictsDashboard = React.lazy(
    () => import("#districts-package/districts-root.tsx"),
);
const DonateAiGuidePage = React.lazy(
    () => import("#donate-ai-guide-package/donate-ai-guide.tsx"),
);
const CancelPayPal = React.lazy(
    () => import("#donate-cancel-recurring-package/cancel-paypal.tsx"),
);
const DonateMatchPage = React.lazy(
    () => import("#donate-package/donate-match-page.tsx"),
);
const DonationAcknowledgement = React.lazy(
    () => import("#donate-package/donation-acknowledgement.tsx"),
);
const DonorsLandingPageModuleList = React.lazy(
    () => import("#donors-landing-package/donors-landing-page-module-list.tsx"),
);
const EmailSubscriptionUnsubscribe = React.lazy(
    () => import("#email-package/email-subscription-unsubscribe.tsx"),
);
const EmailSubscriptionUnsubscribed = React.lazy(
    () => import("#email-package/email-subscription-unsubscribed.tsx"),
);
const CanvasDynamicRegistrationButton = React.lazy(
    () =>
        import(
            "#guide-canvas-integration/canvas-dynamic-registration-button.tsx"
        ),
);
const CanvasCleverFinalizer = React.lazy(
    () => import("#guide-canvas-integration/canvas-clever-finalizer.tsx"),
);
const CanvasCleverInitializer = React.lazy(
    () => import("#guide-canvas-integration/canvas-clever-initializer.tsx"),
);
const CanvasIntegration = React.lazy(
    () => import("#guide-canvas-integration/canvas-integration.tsx"),
);
const CanvasLoginPage = React.lazy(
    () => import("#guide-canvas-integration/canvas-login.tsx"),
);
const CanvasLoginFlow = React.lazy(
    () => import("#guide-canvas-integration/canvas-login-flow.tsx"),
);
const CanvasTOSPage = React.lazy(
    () => import("#guide-canvas-integration/canvas-tos-page.tsx"),
);
const HourOfCodePage = React.lazy(
    () => import("#hour-of-code-package/hour-of-code-page.tsx"),
);
const KhanLabsConversionTrial = React.lazy(
    () =>
        import("#khan-labs-conversion-package/khan-labs-conversion-trial.tsx"),
);
const KhanLabsInvitation = React.lazy(
    () => import("#khan-labs-invitation-package/khan-labs-invitation.tsx"),
);
const KhanKidsLandingPage = React.lazy(
    () => import("#khankids-package/khankids.tsx"),
);
const KhanKidsPrivacy = React.lazy(
    () => import("#khankids-package/khan-kids-privacy.tsx"),
);
const FreeForTeachersEnroll = React.lazy(
    () => import("#khanmigo-package/free-for-teachers-enroll.tsx"),
);
const KhanmigoCheckout = React.lazy(
    () => import("#khanmigo-package/khanmigo-checkout.tsx"),
);
const KhanmigoThankYou = React.lazy(
    () => import("#khanmigo-package/khanmigo-thank-you.tsx"),
);
const LearnPrototype = React.lazy(
    () => import("#learn-prototype/learn-prototype.tsx"),
);
const LearnStormCampaignEnroll = React.lazy(
    () =>
        import(
            "#learnstorm-campaign-enroll-package/learnstorm-campaign-enroll.tsx"
        ),
);
const LearnstormClassDashboard = React.lazy(
    () => import("#learnstorm-dashboard-package/class-dashboard.tsx"),
);
const LearnstormDemoDashboard = React.lazy(
    () => import("#learnstorm-dashboard-package/demo-dashboard.tsx"),
);
const LiteVideo = React.lazy(
    () => import("#lite-homepage-package/lite-video.tsx"),
);
const GuideActivities = React.lazy(
    () =>
        import(
            "#logged-in-homepage-package/content-pages/guide/guide-activities.tsx"
        ),
);
const LoggedInHomepage = React.lazy(
    () => import("#logged-in-homepage-package/logged-in-homepage.tsx"),
);
const MasteryTowerPage = React.lazy(
    () => import("#mastery-tower-package/mastery-tower-page.tsx"),
);
const ParentDashboard = React.lazy(
    () => import("#parent-dashboard-package/parent-dashboard-wrapper.tsx"),
);
const PerseusPreviewRoutes = React.lazy(
    () => import("#perseus-preview-package/perseus-preview-routes.tsx"),
);
const EmbeddedPythonUserProgramPage = React.lazy(
    () =>
        import("#python-coding/programs/embedded-python-user-program-page.tsx"),
);
const PythonUserProgramPage = React.lazy(
    () => import("#python-coding/programs/python-user-program-page.tsx"),
);
const AmbassadordLanding = React.lazy(
    () => import("#referrals-package/ambassador-landing.tsx"),
);
const RegionalHubPage = React.lazy(
    () => import("#regional-standards-package/regional-hub-page.tsx"),
);
const RegionalStandardsPage = React.lazy(
    () => import("#regional-standards-package/regional-standards-page.tsx"),
);
const StandardsMappingPage = React.lazy(
    () => import("#regional-standards-package/standards-mapping-page.tsx"),
);
const DigitalOspLandingPage = React.lazy(
    () =>
        import(
            "#sat-landing-package/containers/digital-sat-landing-page-container.tsx"
        ),
);
const DigitalSatStartRedirectPage = React.lazy(
    () =>
        import(
            "#sat-landing-package/containers/digital-sat-start-redirect-page-container.tsx"
        ),
);
const LinkRedirector = React.lazy(
    () => import("#scratchpads-package/link-redirector.tsx"),
);
const SearchPage = React.lazy(() => import("#search-package/search-page.tsx"));
const SettingsPage = React.lazy(
    () => import("#settings-package/settings-page.tsx"),
);

const StoriesContainer = React.lazy(
    () => import("#stories-package/stories-container.tsx"),
);
const SupportInfo = React.lazy(() => import("#support-info/support-info.tsx"));
const TeacherCampaignResponsePage = React.lazy(
    () => import("#teacher-campaign-package/response-page.tsx"),
);
const TeacherCampaignVideoPage = React.lazy(
    () => import("#teacher-campaign-package/video-page.tsx"),
);
const AssignmentRedirect = React.lazy(
    () => import("./redirects/assignment-redirect.tsx"),
);
const QuickAssignShell = React.lazy(
    () => import("#auth/components/quick-assign-shell/quick-assign-shell.tsx"),
);
const TeachersLandingPage = React.lazy(
    () =>
        import(
            "#teachers-landing-package/teachers-landing-page-module-list.tsx"
        ),
);
const EmbeddedScratchpadPage = React.lazy(
    () => import("#tutorial-scratchpad-package/embedded-scratchpad-page.tsx"),
);
const ExistingUserProgramPage = React.lazy(
    () => import("#tutorial-scratchpad-package/existing-user-program-page.tsx"),
);
const NewUserProgramPage = React.lazy(
    () => import("#tutorial-scratchpad-package/new-user-program-page.tsx"),
);
const EmbedVideoPage = React.lazy(
    () => import("#video-package/embed-video-page.tsx"),
);
const SimpleTranscript = React.lazy(
    () => import("#video-package/simple-transcript.tsx"),
);
const WalmartOnboardingPage = React.lazy(
    () => import("#walmart-package/walmart-onboarding-page.tsx"),
);

const NotFound = React.lazy(() => import("./errors/not-found.tsx"));

const ContinueRedirect = React.lazy(
    () => import("./redirects/continue-redirect.tsx"),
);
const CustomRedirect = React.lazy(
    () => import("./redirects/custom-redirect.tsx"),
);
const NontrivialRedirectRoutes = React.lazy(
    () => import("./redirects/nontrivial-redirect-routes.tsx"),
);

const AboutPage = React.lazy(() => import("#about-appshell/about.tsx"));
const DistrictsTosAcceptedRoute = React.lazy(
    () => import("#districts-package/districts-tos-accepted-page.tsx"),
);

const KhanmigoAssignmentsCustomInputsComponent = React.lazy(
    () =>
        import(
            "#class-package/content/khanmigo-assignments-custom-inputs-component.tsx"
        ),
);

const LearnerBirthdayPage = React.lazy(
    () => import("#auth/new_components/learner-birthday-page.tsx"),
);

const LearnerSignupPage = React.lazy(
    () => import("#auth/new_components/learner-signup-page.tsx"),
);

const LearnerUnderageSignupPage = React.lazy(
    () => import("#auth/new_components/learner-underage-signup-page.tsx"),
);

const TeacherSignupPage = React.lazy(
    () => import("#auth/new_components/teacher-signup-page.tsx"),
);

const ParentSignupPage = React.lazy(
    () => import("#auth/new_components/parent-signup-page.tsx"),
);

const ExtraInfoPage = React.lazy(
    () => import("#auth/new_components/extra-info-page.tsx"),
);

const DistrictsTosRoute = React.lazy(
    () => import("#districts-package/districts-tos-page.tsx"),
);

const VideoPlayerTestPage = React.lazy(
    () => import("#video-package/video-player-test-page.tsx"),
);

const ImportNewDatasetPage = React.lazy(
    () => import("#school-picker-admin-package/import-new-dataset-page.tsx"),
);

const ViewDatasetPage = React.lazy(
    () => import("#school-picker-admin-package/view-dataset-page.tsx"),
);

const PreservationApprovalPage = React.lazy(
    () =>
        import(
            "#districts-package/account-preservation/preservation-approval-page.tsx"
        ),
);

const PreservationApprovalSuccessPage = React.lazy(
    () =>
        import(
            "#districts-package/account-preservation/preservation-approval-success-page.tsx"
        ),
);

const AssessmentsTeacherDashboard = React.lazy(
    () => import("#assessments-management/pages/teacher-dashboard-page.tsx"),
);
const AssessmentsSeriesSelectionPage = React.lazy(
    () => import("#assessments-management/pages/series-selection-page.tsx"),
);
const AssessmentsClassAssociationPage = React.lazy(
    () => import("#assessments-management/pages/class-association-page.tsx"),
);

const ListDatasetsPage = React.lazy(
    () => import("#school-picker-admin-package/list-datasets-page.tsx"),
);

const KadssRoutes = React.lazy(
    () => import("#districts-package/self-serve/router.tsx"),
);

const AssessmentsReportingStudentReportPage = React.lazy(
    () => import("#assessments-management/pages/student-report-page.tsx"),
);

const AssessmentsReportingClassReportPage = React.lazy(
    () => import("#assessments-management/pages/class-report-page.tsx"),
);
const AssessmentsManagementAdminLandingPage = React.lazy(
    () => import("#assessments-management/pages/admin-landing-page.tsx"),
);

/**
 * Renders the routing switch that chooses which top-level route to render.
 */
export default (): React.ReactElement => (
    <Switch>
        {/**
         * TODO: Tidy these up into groups of like things to make this
         * easier to navigate. Move routes that share a leading slug
         * into subroute tables (that will also mean less loadable
         * definitions loaded up front). Finally, develop a way of
         * collecting related routes together (like "all redirects")
         * that works with the Switch (or our own Switch
         * component - it's just a loop over children) so that we can
         * make this file a little more manageable.
         */}
        <TopLevelRoute
            /**
             * NOTE: redirects can be of the form `/r/thing/thing`
             * if folks want, so we have to cater for that; hence, the
             * trailing (.*) here telling our `slug` param to swallow
             * all the remainder of the URL. This then becomes a `slug`
             * prop on our component.
             */
            path="/r/:slug(.*)?"
            exact={false}
            component={CustomRedirect}
        />
        <TopLevelRoute path="/video" exact={true} component={LiteVideo} />
        <TopLevelRoute
            path="/about/alumni"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={OurAlumni}
        />
        <TopLevelRoute
            path="/about/api-tos"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={NotFound}
        />
        <TopLevelRoute
            path="/ambassador"
            exact={true}
            component={AmbassadordLanding}
        />
        <TopLevelRoute
            path="/badges/:slug"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={BadgesPage}
        />
        <TopLevelRoute
            path="/badges"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={BadgesPage}
        />
        <TopLevelRoute path="/contribute" exact={true} component={Contribute} />
        <TopLevelRoute
            path="/contribute/credits"
            exact={true}
            component={ContributeCreditsPage}
        />
        <TopLevelRoute
            path="/about/our-supporters"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={OurSupporters}
        />
        <TopLevelRoute
            path="/downloads"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={DownloadsPage}
        />
        <TopLevelRoute
            path="/careers/interns"
            exact={true}
            component={Interns}
        />
        <TopLevelRoute path="/careers" exact={true} component={Careers} />
        <TopLevelRoute
            path="/khan-labs/join/:invitationToken"
            exact={true}
            component={KhanLabsInvitation}
        />
        <TopLevelRoute
            path="/khan-labs/trial/join"
            exact={true}
            component={KhanLabsConversionTrial}
        />
        <TopLevelRoute
            path="/khanmigo/checkout"
            exact={true}
            component={KhanmigoCheckout}
        />
        <TopLevelRoute
            path="/khanmigo/thank-you"
            exact={true}
            component={KhanmigoThankYou}
        />
        <TopLevelRoute
            path="/khanmigo/teacher-tools"
            exact={true}
            component={FreeForTeachersEnroll}
        />
        <TopLevelRoute
            path="/teacher/blooket"
            exact={true}
            component={BlooketLanding}
        />
        <SmartRedirect
            // Currently there is only one referral program: the
            // Khanmigo for Teachers referral program. So any received
            // referral codes can just be directly routed to the
            // for-teachers sign-up flow.
            //
            // If additional referral programs are added this route
            // may need to change to a page which loads the referral
            // code, determines which program it is part of and
            // redirects as appropriate.
            from="/referrals/:referralCode"
            to="/khanmigo/teacher-tools?referralCode=:referralCode"
        />
        <TopLevelRoute
            path="/redirect"
            exact={true}
            component={ContinueRedirect}
        />
        <TopLevelRoute
            path="/about/privacy-policy"
            exact={false}
            component={PrivacyPolicy}
        />
        <TopLevelRoute
            path="/about/accessibility-statement"
            exact={false}
            component={AccessibilityStatementPage}
        />
        <TopLevelRoute
            path="/about/cookie-policy"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={Cookie}
        />
        <TopLevelRoute
            path="/about/our-board"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={OurBoard}
        />
        <TopLevelRoute
            path="/about/our-content-specialists"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={OurSpecialists}
        />
        <TopLevelRoute
            path="/about/our-interns"
            exact={false}
            component={OurInterns}
        />
        <TopLevelRoute
            path="/about/tos-clever-lib"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={TermsOfServiceClever}
        />
        <TopLevelRoute
            path="/about/the-team"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={TheTeam}
        />
        <TopLevelRoute
            path="/about/dmca"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={Dmca}
        />
        <TopLevelRoute
            path="/about/docs/:slug"
            exact={true}
            component={AboutDocs}
        />
        <TopLevelRoute
            path="/about"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={AboutPage}
        />
        <TopLevelRoute
            path="/donate/khanmigo"
            exact={true}
            component={DonateAiGuidePage}
        />
        <TopLevelRoute
            path="/donate/match"
            exact={true}
            component={DonateMatchPage}
        />
        <TopLevelRoute
            path="/donate/thanks"
            exact={true}
            component={DonationAcknowledgement}
        />
        <TopLevelRoute
            path="/kids/privacy-policy"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={KhanKidsPrivacy}
        />
        <TopLevelRoute
            path="/kids"
            // NOTE(csilvers): When ka-locale is English, /kids gets
            // redirected to webflow in fastly-khanacademy-compute.
            // We only get here if the ka-locale is not English.
            exact={true}
            component={KhanKidsLandingPage}
        />
        <TopLevelRoute
            path="/search"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={SearchPage}
        />
        <TopLevelRoute
            path="/stories"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={StoriesContainer}
        />
        <LocaleRequiredRoute
            locales={["pt"]}
            path="/brasil"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={BnccPage}
        />
        <TopLevelRoute
            path="/commoncore/:standardId"
            exact={true}
            component={NontrivialRedirectRoutes}
        />
        <TopLevelRoute
            path="/:domain/:course/help-requests"
            exact={true}
            component={HelpRequestsPage}
        />
        <TopLevelRoute
            path="/:domain/:course/:type(d|t)"
            exact={false}
            component={CourseDiscussionWrapper}
        />
        <TopLevelRoute
            path="/transcript/:videoId"
            exact={false}
            component={SimpleTranscript}
        />
        <LocaleRequiredRoute
            locales={["es"]}
            path="/curriculonacional"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={CnccPage}
        />
        <TopLevelRoute
            path="/join/districts/:classCode/email"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={DistrictsJoinEmailPage}
        />
        <TopLevelRoute
            path="/join/districts/:classCode/login"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={DistrictsLoginPage}
        />
        <TopLevelRoute
            path="/join/districts/:classCode/signup"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={DistrictsSignupPage}
        />
        <TopLevelRoute
            path="/join/districts/:classCode/finish"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={DistrictsJoinFinishPage}
        />
        <TopLevelRoute
            path="/join/districts/:classCode/school-login"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={DistrictsSchoolLoginPage}
        />
        <TopLevelRoute
            path="/join/districts/:classCode/account-choice"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={DistrictsAccountChoicePage}
        />
        <TopLevelRoute
            path="/join/districts/:classCode/disconnect-school"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={DistrictsDisconnectSchoolEmailPage}
        />
        <TopLevelRoute
            path="/join/districts/:classCode"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={DistrictsJoinStartPage}
        />
        <Route
            path="/login/clever/:redirectType"
            component={CleverRedirectPage}
        />
        <Route
            path="/login/(classlink|classlink_dev)"
            component={ClasslinkRedirectPage}
        />
        <TopLevelRoute
            path="/login/walmart/:stepId"
            exact={true}
            component={WalmartOnboardingPage}
        />
        <TopLevelRoute
            path="/canvas/install-khanmigo-teacher-tools"
            exact={true}
            component={CanvasDynamicRegistrationButton}
        />
        <TopLevelRoute
            path="/login/canvas"
            exact={true}
            component={CanvasLoginPage}
        />
        <TopLevelRoute
            path="/login/canvas/flow"
            exact={true}
            component={CanvasLoginFlow}
        />
        <TopLevelRoute
            path="/login/canvas/clever"
            exact={true}
            component={CanvasCleverInitializer}
        />
        <TopLevelRoute
            path="/login/canvas/clever/:redirectType"
            exact={true}
            component={CanvasCleverFinalizer}
        />
        <TopLevelRoute
            path="/teacher/canvas-terms"
            exact={true}
            component={CanvasTOSPage}
        />
        <TopLevelRoute
            path="/signup/learner/birthday"
            exact={true}
            component={LearnerBirthdayPage}
        />
        <TopLevelRoute
            path="/signup/learner/over-age-gate"
            exact={true}
            component={LearnerSignupPage}
        />
        <TopLevelRoute
            path="/signup/learner/under-age-gate"
            exact={true}
            component={LearnerUnderageSignupPage}
        />
        <TopLevelRoute
            path="/signup/teacher"
            exact={true}
            component={TeacherSignupPage}
        />
        <TopLevelRoute
            path="/signup/parent"
            exact={true}
            component={ParentSignupPage}
        />
        <TopLevelRoute
            path="/signup/extra-info"
            exact={true}
            component={ExtraInfoPage}
        />
        <TopLevelRoute path="/kadss" exact={false} component={KadssRoutes} />

        {/* The auth routes have to come after all other /login/* and /join/* routes */}
        <TopLevelRoute
            path="/(login|join|join-class|logout|forgotpw|pwreset|signup|invitesignup|completesignup|transfer_auth)"
            exact={false}
            component={AuthRoutes}
        />
        {/* Preservation paths */}
        <TopLevelRoute
            path="/preservation/approval"
            exact={true}
            component={PreservationApprovalPage}
        />
        <TopLevelRoute
            path="/preservation/approval/success"
            exact={true}
            component={PreservationApprovalSuccessPage}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/assessments-management/reporting/:assessmentId/student/:studentKaid/performance"
            exact={true}
            component={AssessmentsReportingStudentReportPage}
        />
        <TopLevelRoute
            path="/assessments-management/reporting/:assessmentId/performance"
            exact={true}
            component={AssessmentsReportingClassReportPage}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/assessments-management/admin"
            exact={true}
            component={AssessmentsManagementAdminLandingPage}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/assessments-management/teacher"
            exact={true}
            component={AssessmentsTeacherDashboard}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/assessments-management/teacher/select"
            exact={true}
            component={AssessmentsSeriesSelectionPage}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/assessments-management/teacher/associate-classes"
            exact={true}
            component={AssessmentsClassAssociationPage}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={true}
            path="/assignment/view/:assignmentId"
            exact={true}
            component={AssignmentRedirect}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={true}
            path="/assignment/:assignmentId"
            exact={true}
            component={QuickAssignShell}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/coach/campaign/:campaignId/videos/:stepId"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={TeacherCampaignVideoPage}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/coach/campaign/:campaignId/response/:stepId"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={TeacherCampaignResponsePage}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/teacher/class/__demo__/learnstorm"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={LearnstormDemoDashboard}
        />
        <ClassroomRedirectRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/teacher/class/:classCode/learnstorm"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={LearnstormClassDashboard}
        />
        <ClassroomRedirectRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/teacher/class/:classCode/edit-your-assignment/:contentID"
            exact={true}
            component={KhanmigoAssignmentsCustomInputsComponent}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/teacher/edit-your-assignment/:contentID"
            exact={true}
            component={KhanmigoAssignmentsCustomInputsComponent}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/learnstorm-campaign/enroll"
            exact={true}
            component={LearnStormCampaignEnroll}
        />
        <ClassroomRedirectRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/teacher/class/:classCode/mastery-tower"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={MasteryTowerPage}
        />
        <ClassroomRedirectRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/teacher/class/:classCode/progress/unit-mastery/subject/:subjectId/unit/:unitId"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={UnitMasteryProgress}
        />
        <TopLevelRoute
            path="/settings/*"
            exact={false}
            component={SettingsPage}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/video-player-test"
            exact={true}
            component={VideoPlayerTestPage}
        />
        <ClassroomRedirectRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/teacher/class/:classCode"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={ClassShell}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            exact={true}
            path="/coach/class/:classId"
            component={CoachClassRouteRedirect}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/coach/class/:classId/*"
            exact={false}
            component={CoachClassRouteRedirect}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/teacher/report/assignment/:assignmentId"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={CoachReport}
        />
        <LoginRequiredRoute
            path="/:domain/:course/:unit/:lesson/:kind(e)/:slug/report"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={ExerciseReport}
            childUserAllowed={true}
            phantomUserAllowed={false}
        />
        <LoginRequiredRoute
            // Note(angelo): Deprecated (May 2022). Replaced by the next route.
            path="/:domain/:course/:unit/:kind(q|u)/:slug/report"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={ExerciseReport}
            childUserAllowed={true}
            phantomUserAllowed={false}
        />
        <LoginRequiredRoute
            path="/:domain/:course/:unit/:lesson/:kind(quiz|test)/:slug/report"
            exact={true}
            component={ExerciseReport}
            childUserAllowed={true}
            phantomUserAllowed={false}
        />
        <LoginRequiredRoute
            path="/:domain/:course/:kind(test)/:slug/report"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={ExerciseReport}
            childUserAllowed={true}
            phantomUserAllowed={false}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/coach(/.*)?"
            exact={false}
            component={Coach}
        />
        <TopLevelRoute
            path="/teacher/document/:documentID/view"
            component={TeacherDocumentPage}
            exact={true}
        />
        <TopLevelRoute
            path="/parent/document/:documentID/view"
            component={TeacherDocumentPage}
            exact={true}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/teacher/khanmigo-tools/:tool"
            exact={false}
            component={AIToolPage}
        />
        <TopLevelRoute
            path="/teacher/khanmigo-tools-canvas"
            component={CanvasIntegration}
            exact={true}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/teacher(/.*)?"
            exact={false}
            component={Coach}
        />
        <TopLevelRoute
            path="/digital-sat/start"
            exact={true}
            component={DigitalSatStartRedirectPage}
        />
        <TopLevelRoute
            path="/digital-sat/:confirmed(confirmed)?"
            exact={true}
            component={DigitalOspLandingPage}
        />
        <Route path="/districts/link" component={DistrictsAccountLinkPage} />
        <LoginRequiredRoute
            path="/districts"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={DistrictsDashboard}
            childUserAllowed={false}
            phantomUserAllowed={false}
        />
        <PermissionRequiredRoute
            permission={{
                capability: "can_view_school_picker_datasets",
            }}
            path="/school-picker-datasets"
            exact={true}
            component={ListDatasetsPage}
        />
        <PermissionRequiredRoute
            permission={{
                capability: "can_import_school_picker_datasets",
            }}
            path="/school-picker-datasets/new"
            exact={true}
            component={ImportNewDatasetPage}
        />
        <PermissionRequiredRoute
            permission={{
                capability: "can_view_school_picker_datasets",
            }}
            path="/school-picker-datasets/:versionID"
            exact={true}
            component={ViewDatasetPage}
        />
        <TopLevelRoute
            path="/perseus"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={PerseusPreviewRoutes}
        />
        <TopLevelRoute
            path="/embed_video"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={EmbedVideoPage}
        />
        <TopLevelRoute
            path="/teachers"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={TeachersLandingPage}
        />
        <TopLevelRoute
            path="/donors"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={DonorsLandingPageModuleList}
        />
        <TopLevelRoute
            path="/donate/cancel-paypal"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={CancelPayPal}
        />
        <TopLevelRoute
            path="/emails/subscriptions/unsubscribed"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={EmailSubscriptionUnsubscribed}
        />
        <TopLevelRoute
            path="/subscriptions/unsubscribe"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={EmailSubscriptionUnsubscribe}
        />
        <TopLevelRoute
            path="/hourofcode"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={HourOfCodePage}
        />
        {/** Computing */}
        <TopLevelRoute
            path="/computer-programming/link_redirector"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={LinkRedirector}
        />
        <TopLevelRoute
            path={`/${PROGRAM_DOMAINS_MATCHING_GROUP}/:slug/:scratchpad_id(\\d+)`}
            component={ExistingUserProgramPage}
            exact={true}
        />
        <TopLevelRoute
            path={`/${PROGRAM_DOMAINS_MATCHING_GROUP}/:slug/:scratchpad_id(\\d+)/embedded`}
            component={EmbeddedScratchpadPage}
            exact={true}
        />
        <TopLevelRoute
            path="/python-program/:slug/:program_id(\d+)"
            component={PythonUserProgramPage}
            exact={true}
        />
        <TopLevelRoute
            path="/python-program/:program_id(\d+)/embedded"
            component={EmbeddedPythonUserProgramPage}
            exact={true}
        />
        <TopLevelRoute
            path="/computing/programs"
            component={ProgramGalleryPage}
            exact={true}
        />
        <TopLevelRoute
            path={`/:domain${PROGRAM_DOMAINS_MATCHING_GROUP}/new/:content_type(pjs|sql|webpage)`}
            component={NewUserProgramPage}
            exact={true}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={true}
            path="/learn"
            exact={false}
            component={LearnPrototype}
        />
        <TopLevelRoute
            path="/check"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={CheckAccess}
        />
        <TopLevelRoute
            path="/support"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={SupportInfo}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={true}
            mobileAppUserAllowed={true}
            path="/profile/me/khanmigo/activities"
            exact={true}
            component={GuideActivities}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={true}
            mobileAppUserAllowed={true}
            path="/profile/me/khanmigo/activities/:activitySlug/:activitySubRouteSlug?"
            exact={true}
            component={GuideActivities}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={true}
            path="/profile/me"
            exact={false}
            component={LoggedInHomepage}
        />
        <HomepageRoute path="/profile(/.*)?" exact={false} />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/parent/khanmigo-tools/:tool"
            exact={false}
            component={AIToolPage}
        />
        <ClassroomRedirectRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/parent/child-assignments/:classCode"
            exact={false}
            component={ClassShell}
        />
        <LoginRequiredRoute
            phantomUserAllowed={false}
            childUserAllowed={false}
            path="/parent(/.*)?"
            exact={false}
            component={ParentDashboard}
        />
        <TopLevelRoute
            path="/exercise/:slug"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={NontrivialRedirectRoutes}
        />
        <TopLevelRoute
            path="/exercises"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
            component={NontrivialRedirectRoutes}
        />
        <TopLevelRoute
            path="*/modal/*"
            exact={false}
            component={NontrivialRedirectRoutes}
        />
        <HomepageRoute
            path="/coaches"
            // NOTE(kevinb): This route should probably use `exact={true}`.
            // Please verify that this change is okay before making it.
            exact={false}
        />
        <HomepageRoute path="/lohp" exact={false} />
        <HomepageRoute path="/" exact={true} />
        {/* Regional Standards Routes */}
        <TopLevelRoute
            path="/regions/:countrySlug"
            exact={true}
            component={RegionalHubPage}
        />
        <TopLevelRoute
            path="/regions/:countrySlug/:regionSlug"
            exact={true}
            component={RegionalStandardsPage}
        />
        <TopLevelRoute
            path="/standards/:setOfStandardsId"
            exact={true}
            component={StandardsMappingPage}
        />
        <TopLevelRoute
            path="/standards/:setOfStandardsId/:standardId"
            exact={true}
            component={StandardsMappingPage}
        />
        <TopLevelRoute
            path="/districts-tos"
            exact={true}
            component={DistrictsTosRoute}
        />
        <TopLevelRoute
            path="/districts-tos-accepted"
            exact={true}
            component={DistrictsTosAcceptedRoute}
        />
        {/* Official Content routes */}
        {/* learnable node routes */}
        {/* sync-start:content-page-routes 1112123001 ./apps/khanacademy/src/content-library/ui/content-page-routes.tsx */}
        <TopLevelContentRoute
            path="/:domain/:course/:unit/:lesson/:kind(v|a|e|p|pc|pi|pp|pt|quiz|test)/:slug"
            component={ContentPageRoutes}
            exact={true}
        />
        <TopLevelContentRoute
            path="/:domain/:course/test/:slug([a-z0-9]+:course-challenge)"
            component={ContentPageRoutes}
            exact={true}
        />
        <TopLevelContentRoute
            path="/:domain/:course/test/:slug([a-z0-9]+:mastery-challenge)"
            component={ContentPageRoutes}
            exact={true}
        />
        {/* sync-end:content-page-routes */}
        <TopLevelContentRoute
            path="/:domain/:course/test/subject-challenge"
            component={ContentRedirect}
            exact={true}
        />
        <TopLevelContentRoute
            path="/:domain/:course/test/mastery-challenge"
            component={ContentRedirect}
            exact={true}
        />
        {/* known redirect routes */}
        {/* normally, we can let content redirect paths fall through to the
        fallback at the very bottom of this file.
        we define this redirect explicitly because it would otherwise
        erroneously match the /:domain/:course route. */}
        <TopLevelContentRoute
            path="/:kind(v|a|e|p|pc|pi|pp|pt)/:slug"
            component={ContentRedirect}
            exact={true}
        />
        <TopLevelContentRoute
            path="/:domain/:kind(v|a|e|p|pc|pi|pp|pt|quiz|test)/:slug"
            component={ContentRedirect}
            exact={true}
        />
        <TopLevelContentRoute
            path="/:domain/:course/:kind(v|a|e|p|pc|pi|pp|pt|quiz|test)/:slug"
            component={ContentRedirect}
            exact={true}
        />
        {/* curation node paths */}
        <TopLevelContentRoute
            path="/:domain"
            component={ContentLibraryDomainPage}
            exact={true}
        />
        <TopLevelContentRoute
            path="/:domain/:course"
            component={ContentLibraryCurationPage}
            exact={true}
        />
        <TopLevelContentRoute
            path="/:domain/:course/:unit"
            component={ContentLibraryCurationPage}
            exact={true}
        />
        <TopLevelContentRoute
            // this is a bit of a hack; this *is* a known redirect,
            // but the redirect logic comes from the front end and not
            // from the content library service, so content redirect
            // will not handle it.
            path="/:domain/:course/:unit/:lesson"
            component={ContentLibraryCurationPage}
            exact={true}
        />
        {/* If we don't find a specific route in content pages,
        then we defer to content library to see if it can
        tell us where to redirect. */}
        <TopLevelContentRoute
            path="*"
            exact={false}
            component={ContentRedirect}
        />
    </Switch>
);
